import { hslToRgb, rgbToHex, hexToRgb, rgbToHsl, getNearestColorName, rgbToCmyk, rgbToOklch } from './colors';
import { generateTints, generateShades, getContrastRatio } from './colorAnalysis';
import type { ExportColorEntry } from './codeExport';

// ── Types ──────────────────────────────────────────

export interface StudioColor {
  id: string;
  hex: string;       // "#RRGGBB"
  locked: boolean;
}

export type HarmonyMode =
  | 'random'
  | 'analogous'
  | 'triadic'
  | 'complementary'
  | 'split-complementary'
  | 'tetradic'
  | 'monochromatic';

export const HARMONY_LABELS: Record<HarmonyMode, string> = {
  'random':              'Random',
  'analogous':           'Analogous',
  'triadic':             'Triadic',
  'complementary':       'Complementary',
  'split-complementary': 'Split Comp.',
  'tetradic':            'Tetradic',
  'monochromatic':       'Monochromatic',
};

// ── Helpers ────────────────────────────────────────

let counter = 0;
const uid = (): string => `sc-${Date.now()}-${++counter}`;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Convert HSL (h: 0-360, s: 0-100, l: 0-100) → "#RRGGBB" */
const hslToHex = (h: number, s: number, l: number): string => {
  const rgb = hslToRgb(((h % 360) + 360) % 360, clamp(s, 0, 100), clamp(l, 0, 100));
  return rgbToHex(rgb.r, rgb.g, rgb.b);
};

/** Random int in [lo, hi] inclusive */
const randInt = (lo: number, hi: number): number =>
  lo + Math.floor(Math.random() * (hi - lo + 1));

// Golden angle for hue distribution (≈137.508°)
const GOLDEN_ANGLE = 137.508;

// ── Palette generation ─────────────────────────────

/**
 * Generate a fresh random palette with well-distributed hues.
 * Uses golden-angle spacing from a random starting hue.
 */
export const generateRandomPalette = (count: number): StudioColor[] => {
  const startHue = Math.random() * 360;
  return Array.from({ length: count }, (_, i) => ({
    id: uid(),
    hex: hslToHex(startHue + i * GOLDEN_ANGLE, randInt(55, 95), randInt(40, 70)),
    locked: false,
  }));
};

/**
 * Regenerate unlocked colors. Locked colors are preserved in-place.
 * In harmony modes, a seed color (first locked, or random) determines the base hue.
 */
export const regeneratePalette = (
  current: StudioColor[],
  harmony: HarmonyMode,
): StudioColor[] => {
  if (harmony === 'random') {
    return current.map(c =>
      c.locked
        ? c
        : { id: uid(), hex: hslToHex(Math.random() * 360, randInt(55, 95), randInt(40, 70)), locked: false },
    );
  }

  // Seed hue: first locked color's hue, or random
  const locked = current.find(c => c.locked);
  const seedRgb = locked ? hexToRgb(locked.hex) : null;
  const seedHue = seedRgb
    ? rgbToHsl(seedRgb.r, seedRgb.g, seedRgb.b).h
    : Math.random() * 360;

  const offsets = computeHarmonyOffsets(harmony, current.length);

  return current.map((c, i) => {
    if (c.locked) return c;
    const h = seedHue + offsets[i];
    const s = randInt(55, 90);
    const l = harmony === 'monochromatic' ? randInt(25, 80) : randInt(40, 70);
    return { id: uid(), hex: hslToHex(h, s, l), locked: false };
  });
};

/** Compute hue offsets for each slot based on harmony mode */
const computeHarmonyOffsets = (mode: HarmonyMode, count: number): number[] => {
  switch (mode) {
    case 'analogous':
      return distributeAround(0, 30, count);        // ±30° increments
    case 'triadic':
      return distributeAround(0, 120, count);       // 120° apart
    case 'complementary':
      return distributeAround(0, 180, count);       // 180° apart
    case 'split-complementary':
      return distributeAround(0, 150, count);       // 150/210°
    case 'tetradic':
      return distributeAround(0, 90, count);        // 90° apart
    case 'monochromatic':
      return Array(count).fill(0);                  // same hue
    default:
      return Array.from({ length: count }, (_, i) => i * GOLDEN_ANGLE);
  }
};

/**
 * Distribute `count` hue offsets evenly around a base,
 * using `step` as the primary angular separation.
 */
const distributeAround = (base: number, step: number, count: number): number[] => {
  if (count <= 1) return [base];
  // Create anchor points at multiples of step
  const anchors: number[] = [];
  for (let i = 0; anchors.length < count; i++) {
    anchors.push(base + i * step);
  }
  return anchors.slice(0, count);
};

// ── Reorder ────────────────────────────────────────

export const reorderPalette = (
  colors: StudioColor[],
  fromIndex: number,
  toIndex: number,
): StudioColor[] => {
  const result = [...colors];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  return result;
};

// ── URL serialization ──────────────────────────────

export const serializePaletteToUrl = (colors: StudioColor[]): string =>
  colors.map(c => c.hex.replace('#', '')).join('-');

export const parsePaletteFromUrl = (param: string): StudioColor[] => {
  const hexes = param.split('-').filter(h => /^[0-9A-Fa-f]{6}$/.test(h));
  if (hexes.length === 0) return generateRandomPalette(5);
  return hexes.map(h => ({ id: uid(), hex: `#${h.toUpperCase()}`, locked: false }));
};

// ── Export conversion ──────────────────────────────

export const studioColorsToExportEntries = (colors: StudioColor[]): ExportColorEntry[] =>
  colors.map(c => {
    const rgb = hexToRgb(c.hex)!;
    const name = getNearestColorName(rgb.r, rgb.g, rgb.b)
      .toLowerCase()
      .replace(/\s+/g, '-');
    return {
      name,
      hex: c.hex,
      tints: generateTints(c.hex, 9),
      shades: generateShades(c.hex, 9),
      hsl: rgbToHsl(rgb.r, rgb.g, rgb.b),
      cmyk: rgbToCmyk(rgb.r, rgb.g, rgb.b),
      oklch: rgbToOklch(rgb.r, rgb.g, rgb.b),
    };
  });

// ── Text color for contrast ────────────────────────

/** Returns true if the color is "light" (use dark text on it) */
export const isLightColor = (hex: string): boolean => {
  const rgb = hexToRgb(hex);
  if (!rgb) return true;
  return getContrastRatio(hex, '#000000') >= getContrastRatio(hex, '#FFFFFF');
};
